import {FileErrorLOFactory} from '../../../externalLogging/factories/FileErrorLOFactory';
import {ExternalLog} from '../../../externalLogging/ExternalLog';
import {LoadingItem} from '../../../loader/LoadingItem';
import { BulkLoader } from './../../../loader/BulkLoader';
import {EnvGlobalsHandler} from "../../EnvGlobalsHandler";
import {PathProvider} from "../../environment/providers/PathProvider";
import {GoodgameLoader} from "../../loading/GoodgameLoader";
import EventDispatcher = createjs.EventDispatcher;
import AbstractLoader = createjs.AbstractLoader;

export class BasicLoaderData extends EventDispatcher {
    private _appLoader: GoodgameLoader = new GoodgameLoader("appLoader");
    private _assetLoader: BulkLoader = new BulkLoader("assetLoader");
    private ASSET_NOT_VERSIONED_FLAG: string = "/undefined";
    /*
     Holds the download rate for all files loaded wihtin in the first frame.
     That includes e.g. the langVersion, networkInfo, itemXML, GLOBAL_COOKIE_SAVER_LOADER, itemXMLVersion and languageXML.
     The format is KB/s
     */
    public downloadRateFrameOne: number;

    constructor() {
        super();
        // TODO: @HTML5 - need an alternative to bulkloader
        //this._assetLoader.logFunction = fatal;
        this._assetLoader.loadQueue.maintainScriptOrder = false;
    }

    /** loader contains all xml and config files. */
    public get appLoader(): GoodgameLoader {
        return this._appLoader;
    }

    /** loader contains all item libs. */
    public get assetLoader(): any {
        return this._assetLoader;
    }

    //##############################################################################################################
    //##Items
    //##############################################################################################################

    /**
     * generates url by using getPath in itemVersions
     * checkAssetUrl and throws/logs errors if its not valid
     *
     * @param assetName:String
     * @return String
     */
    public /*final*/ getVersionedItemAssetUrl(assetName: string): string {
        let itemUrl: string;
        this.checkAssetUrlAndDisplayErrors(assetName, this.bindFunction(this.isItemAssetVersioned), "ITEMVERSIONS");
        itemUrl = PathProvider.instance.getAssetURL(assetName);
        this.checkAssetUrl(itemUrl, assetName, "ITEMVERSIONS");
        return itemUrl;
    }

    /**
     * generateItemAssetUrl
     * checks url for null
     *
     * @param assetName:String
     * @return Boolean
     */
    public isItemAssetVersioned(assetName: string): boolean {
        let assetUrl: string;
        assetUrl = PathProvider.instance.getAssetURL(assetName);
        let isVersioned: boolean = this.checkAssetUrlForNotNull(assetUrl);
        return isVersioned;
    }

    /**
     * generates url by using getPath in itemVersions
     * executes loadAsset
     *
     * @param assetName:String
     * @param prio:int
     * @return LoadingItem
     *
     */
    public loadVersionedItemAsset(assetName: string, prio: number = 0): LoadingItem {
        let assetUrl: string = this.getVersionedItemAssetUrl(assetName);
        return this.loadAsset(assetUrl, assetName, prio);
    }

    //##############################################################################################################
    //##Interface
    //##############################################################################################################

    /**
     * generates url by using getPath in interfaceVersions
     * checkAssetUrl and throws/logs errors if its not valid
     *
     * @param assetName:String
     * @return String
     */
    public getVersionedInterfaceAssetUrl(assetName: string): string {
        let interfaceUrl: string;
        this.checkAssetUrlAndDisplayErrors(assetName, this.bindFunction(this.isInterfaceAssetVersioned), "INTERFACEVERSIONS");
        interfaceUrl = PathProvider.instance.getInterfaceAssetURL(assetName);
        this.checkAssetUrl(interfaceUrl, assetName, "INTERFACEVERSIONS");
        return interfaceUrl;
    }

    /**
     * generateInterfaceAssetUrl
     * checks url for null
     *
     * @param assetName:String
     * @return Boolean
     */
    public isInterfaceAssetVersioned(assetName: string): boolean {
        let assetUrl: string;
        assetUrl = PathProvider.instance.getInterfaceAssetURL(assetName);
        let isVersioned: boolean = this.checkAssetUrlForNotNull(assetUrl);
        return isVersioned;
    }

    /**
     * generates url by using getPath in interfaceVersions
     * executes loadAsset
     *
     * @param assetName:String
     * @param prio:int
     * @return LoadinItem
     *
     */
    public loadVersionedInterfaceAsset(interfaceName: string, prio: number = 0): LoadingItem {
        let interfaceUrl: string = this.getVersionedInterfaceAssetUrl(interfaceName);
        return this.loadAsset(interfaceUrl, interfaceName, prio);
    }

    //##############################################################################################################
    //##General
    //##############################################################################################################

    /**
     * checks path including null
     * throws/logs Errors if asset not versioned
     *
     * @param errorMsg:String
     * @param assetName:String
     * @param itemUrl:String
     * @return Boolean
     *
     */
    private checkAssetUrlAndDisplayErrors(assetName: string, validFunction: Function, errorMsg: string): void {
        if (!validFunction.call(this, assetName)) {
            if (EnvGlobalsHandler.globals.isTest || EnvGlobalsHandler.globals.isTest) {
                // TODO: @HTML5 - need a new logging system
                //LogChannels.loading.error("ASSET_NOT_VERSIONED_IN_" + errorMsg + ": " + assetName);
                throw new Error("ASSET_NOT_VERSIONED_IN_" + errorMsg + ": " + assetName);
            }
            ExternalLog.logger.log(new FileErrorLOFactory(FileErrorLOFactory.ASSET_NOT_VERSIONED_FILE_ERROR, assetName));
        }
    }

    /**
     * checks path including null
     *
     * @param assetName:String
     * @return Boolean
     *
     */
    private checkAssetUrlForNotNull(assetUrl: string): boolean {
        let isValid: boolean

        if (assetUrl.indexOf(this.ASSET_NOT_VERSIONED_FLAG) == -1) {
            isValid = true;
        }
        else {
            isValid = false;
        }

        return isValid;
    }

    /**
     * checks path including null
     * loads Item by using assetLoader
     * logs Errors if asset not versioned
     *
     * @param assetName:String
     * @param itemUrl:String
     * @return Boolean
     *
     */
    private checkAssetUrl(itemUrl: string, assetName: string, errorMsg: string): boolean {
        let isValid: boolean;
        if (itemUrl.indexOf(this.ASSET_NOT_VERSIONED_FLAG) == -1) {
            isValid = true;
        }
        else {
            isValid = false;
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.error("ASSET_NOT_VERSIONED_IN_" + errorMsg + ": " + assetName);
            ExternalLog.logger.log(new FileErrorLOFactory(FileErrorLOFactory.ASSET_NOT_VERSIONED_FILE_ERROR, assetName));
        }
        return isValid;
    }

    /**
     * checks path including null
     * loads Item by using assetLoader
     * logs Errors if asset not versioned
     *
     * @param itemUrl:String
     * @param assetName:String
     * @param prio:int
     * @return LoadingItem
     *
     */

    private loadAsset(itemUrl: string, assetName: string, prio: number = 0): LoadingItem {
        let loadingItem: LoadingItem;
        if (this.checkAssetUrlForNotNull(itemUrl)) {
            // LogChannels.loading.info("Loading Asset " + itemUrl + "(" + assetName + ")");
            let loadProperties: Object = {id: assetName, priority: prio};
            loadingItem = this.assetLoader.addAssetBundle(itemUrl, loadProperties);
        }
        return loadingItem;
    }

    public wasAssetLoaded(assetName: string): boolean {
        if( this.isInterfaceAssetVersioned( assetName )) {
            let interfaceUrl: string = this.getVersionedInterfaceAssetUrl(assetName);
            return this._assetLoader.isLoaded(interfaceUrl);
        }
        else if( this.isItemAssetVersioned( assetName ) ) {
            let assetUrl: string = this.getVersionedItemAssetUrl(assetName);
            return  this._assetLoader.isLoaded(assetUrl);
        }
        return false;
    }
}

